import { podcastType } from '@/components/Layout/EmbedOmny';
import { QuotedNewsProps } from '@/components/Layout/QuotedNews';
import { ArticleType, EditorialBanner, EditorialPartner } from '@/pages/[categorias]/[...news]';
import { WidgetType } from '@/types/types';
import { DealsProps } from '@/components/Layout/DealsBox';
import LazyLoad from 'react-lazyload';
import { getCurrentHub } from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import IFrameYoutube from '@/components/Layout/IFrameYoutube';
import HeaderImage from '../HeaderImage';
import NewsContent from '../NewsContent';
import classNames from '@/utils/className';
import InternalShareButton from '@/components/Layout/InternalShareButton';
import NewsLetter from '../Widgets/NewsLetter/NewsLetter';

const BlackFridayCall = dynamic(() => import('../Widgets/BlackFridayCall'), { ssr: false });
const EmbedOmny = dynamic(() => import('@/components/Layout/EmbedOmny'));
const PodcastAggregators = dynamic(() => import('../Widgets/PodcastAggregators'));
const QuotedNewsBox = dynamic(() => import('@/components/Layout/QuotedNews').then(mod => mod.QuotedNewsBox), { ssr: false });
const EditorialPartnersBanner = dynamic(() => import('@/components/Layout/EditorialPartnersBanner'));

interface MainContentProps {
  headerImgData?: {
    url: string;
    /**
     * Texto alternativo (a11y) — propagado para o `<img alt>` da capa.
     * Quando ausente, o `HeaderImage` cai no fallback `caption` quando for
     * string simples, ou `''` quando for estrutura complexa (array de
     * `ComponentProps`). O type guard interno evita serializar caption
     * rica como `'[object Object]'`.
     */
    alt?: string;
    caption: string;
    blurhash: string;
    srcset: { [key: number]: string };
  };
  article: ArticleType;
  fonte?: string;
  mainCategory?: string;
  video?: {
    id: string;
    idYoutube: string;
    titulo: string;
    descricao: string;
    views: string;
    status: string;
    canal: string;
  };
  capa: {
    blurhash: string;
    srcset?: { [key: number]: string };
    url: string;
    dimensao: {
      largura: number;
      altura: number;
    };
  };
  podcast?: podcastType;
  citados?: QuotedNewsProps[];
  titulo?: string;
  description?: string;
  url?: string;
  type?: string;
  widget?: WidgetType;
  widgetBf?: boolean;
  placeholderAds?: boolean;
  shouldRenderAdsGoogle?: boolean;
  isSubDomain?: boolean;
  flags?: number;
  precos?: DealsProps[];
  editoria?: EditorialBanner | null;
  parceiros?: EditorialPartner[];
  isHeader?: 'internal' | 'full';
  isNewsOpen?: boolean;
  enableNewsLetter?: boolean;
}

export default function MainContainer({
  headerImgData,
  article,
  fonte = '',
  mainCategory = '',
  video = null,
  podcast = null,
  citados = null,
  titulo = '',
  type = '',
  capa = null,
  widget = null,
  widgetBf = false,
  placeholderAds = false,
  shouldRenderAdsGoogle = true,
  isSubDomain = false,
  precos = null,
  flags = 0,
  editoria = undefined,
  parceiros = undefined,
  isHeader = undefined,
  description = '',
  url = '',
  isNewsOpen = false,
  enableNewsLetter = false,
}: MainContentProps) {
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const mainContentSpan = currentSpan && currentSpan.startChild({ op: 'mainContent' });
  const hasPartnerLinks = (flags & 512) !== 0;
  // eslint-disable-next-line no-bitwise
  const isAdvertorial = type === 'artigo' && (flags & 2) !== 0;

  return (
    <div className={classNames('[grid-column:1] desktop:[grid-column-end:2] desktop:[grid-column-start:1]')}>
      <div className="mb-12">
        <InternalShareButton title={titulo} description={description} url={url} isAdvertorial={isAdvertorial} />
        {/* Tarja da editoria, data-driven pelo Directus (cor + logo da editoria
            da categoria). Sem logo cadastrada → editoria sem identidade visual
            → sem tarja. */}
        {editoria?.logo?.url && <EditorialPartnersBanner editoria={editoria} parceiros={parceiros ?? []} />}
        {headerImgData?.url && !video && (
          <HeaderImage
            url={headerImgData.url}
            srcset={headerImgData.srcset}
            alt={headerImgData.alt}
            caption={headerImgData.caption || titulo}
            hasTarja={!!editoria?.logo?.url}
            isHeader={isHeader}
          />
        )}
        {video?.idYoutube && <IFrameYoutube videoTitle={video.titulo} videoId={video.idYoutube} capa={capa} lazy />}
        {podcast && <EmbedOmny titulo={titulo} dataPodcast={podcast} />}
        {podcast && <PodcastAggregators aggregatorsData={article.botoes} dataPodcast={podcast} />}
        <NewsContent
          dataContent={article}
          widget={widget}
          placeholderAds={placeholderAds}
          shouldRenderAdsGoogle={shouldRenderAdsGoogle}
          isSubDomain={isSubDomain}
          product={precos && precos.length > 0 && precos[0]}
          titulo={titulo}
          hasPartnerLinks={hasPartnerLinks}
          isNewsOpen={isNewsOpen}
          mainCategory={mainCategory}
          fonte={fonte}
        />
        {/* Exclusiva do mobile: no desktop a newsletter vive na sidebar (SideContent). */}
        {enableNewsLetter && <NewsLetter />}
        {(hasPartnerLinks || !!citados?.length || widgetBf) && (
          <LazyLoad height={1}>
            {hasPartnerLinks && (
              <div className="mt-6 rounded bg-mglGray200 p-4 text-xs font-light text-mglGray500">
                <p>
                  <span>
                    Este artigo possui links de lojas parceiras do Canaltech. Se você comprar o produto sugerido, podemos receber uma pequena comissão. O
                    programa de afiliados não influencia nosso conteúdo editorial, que tem as liberdades de imprensa e de opinião garantidas.
                  </span>
                </p>
              </div>
            )}

            {!!citados?.length && <QuotedNewsBox citados={citados} type={video ? 'video' : 'podcast'} />}

            {widgetBf && <BlackFridayCall />}
          </LazyLoad>
        )}
      </div>
      {mainContentSpan && mainContentSpan.finish()}
    </div>
  );
}
